import _ from 'lodash';

// This is a handy mixin that can be used globally (no need to import).
// it is a recursive search that SHOULD find what you need out of any large
// nested JSON object tree. It will return 5 values, the 'found' object, it's
// parent object', the array it belongs to, it's index in that array and an
// array of it's descendants starting from the entry.
//
// Usage: It takes 3 params
/*

this._goFind({
  entry: someArrayOfObjects,
  key: '_id',
  value: 'my-id',
});

// Promise usage
this._waitToFind({
  entry: someArrayOfObjects,
  key: '_id',
  value: 'my-id',
}).then((results) => {
   ...do something with the results
});

*/
// This should help with deeply nested droplets (puddles in puddles etc). We shouldn't be limited.

// Named using _, so these global method names won't clash with future local methods
const searchParty = {
  computed: {
    _page() {
      return this.$store.getters.currentPage.blocks;
    },
  },
  methods: {
    _extends(defaults, options) {
      const extended = {};
      let prop;
      for (prop in defaults) {
        if (Object.prototype.hasOwnProperty.call(defaults, prop)) {
          extended[prop] = defaults[prop];
        }
      }
      for (prop in options) {
        if (Object.prototype.hasOwnProperty.call(options, prop)) {
          extended[prop] = options[prop];
        }
      }
      return extended;
    },
    _goFind(opts) {
      const defaults = {
        entry: this._page,
        key: '_id',
        value: '',
      };
      let options;
      if (!_.isUndefined(opts)) {
        // combine passed in options
        options = this._extends(defaults, opts);
      } else {
        options = defaults;
      }
      let found = false;
      let parent;
      let parentArray;
      let index;
      const descendants = [];
      // This will recursively search any json tree until { key: value } is found
      if (!_.isUndefined(options.entry)) {
        options.entry.some(function iter(obj, idx, arr) { // eslint-disable-line
          if (
            Object.prototype.hasOwnProperty.call(obj, options.key) &&
            obj[options.key] === options.value
          ) {
            found = obj;
            index = idx;
            parentArray = arr;
          }
          // If nothing was found in the first pass, keep going...
          if (!found) {
            for (const property in obj) {
              if (Object.prototype.hasOwnProperty.call(obj, property)) {
                const p = obj[property];
                // If property is an array, send through again
                if (Array.isArray(p)) {
                  // best shot at finding the parent obj, this needs
                  // work however because it gets the last checked obj
                  // and not always the correct parent
                  parent = obj;
                  descendants.push(obj);
                  p.some(iter);
                }
              }
            }
          }
        });
      }
      // uncomment as needed
      // log('Search Party results:', { found, parent, descendants, index });
      return { found, parent, parentArray, descendants, index };
    },
    _findAllPropVals(opts, collect) {
      // loops an array of objects only
      _.forEach(opts.entry, (item) => {
        _.forOwn(item, (value, key) => {
          if (key === opts.key &&
              !value.match(opts.exclude) &&
              !collect.includes(value)) {
            collect.push(value);
          }
          // keep searching for props until the end
          // to find the keys in question
          if (_.isArray(item[key])) {
            this._findAllPropVals({
              entry: item[key],
              key: opts.key,
            });
          }
        });
      });
    },
    // usage: this function will find all objects with the key/value
    // options then pass it to a param function.
    //   this._findAndRun({
    //     entry: someArray,
    //     key: 'someKey',
    //     value: 'someVal',
    //   }, someFn);
    _findAndRun(opts, fn) {
      // loops an array of objects only
      _.forEach(opts.entry, (item) => {
        _.forOwn(item, (value, key) => {
          if (key === opts.key && value === opts.value) {
            fn(item, opts.entry);
          }
          // keep searching for props until the end
          // to find the keys in question
          if (_.isArray(item[key])) {
            this._findAndRun({
              entry: item[key],
              key: opts.key,
              value: opts.value,
            }, fn);
          }
        });
      });
    },
    async _deepFind(opts, fn) {
      // loops an array of objects only
      await Promise.all(_.map(opts.entry, async (item) => {
        await Promise.all(_.map(item, async (value, key) => {
          let hasKey = opts.key && key === opts.key && !opts.value;
          let hasVal = opts.value && value === opts.value && !opts.key;
          if (opts.matches) {
            hasKey = opts.key && key.match(opts.key) && !opts.value;
            hasVal = opts.value && value && value.toString().match(opts.matches) && !opts.key;
          }
          // if only key is specified
          if (hasKey) {
            // run callbacks as needed
            if (fn) {
              await fn(item);
            }
          }
          // if only val is specified
          if (hasVal) {
            // run callbacks as needed
            if (fn) {
              await fn(item, key);
            }
          }
          // if matches both kay and value
          if (hasVal && hasKey) {
            // run callbacks as needed
            if (fn) {
              await fn(item);
            }
          }
          // keep searching for props until the end
          // to find the keys in question
          if (_.isPlainObject(value)) {
            const options = {
              entry: [value],
              key: opts.key,
              value: opts.value,
              matches: opts.matches,
            };
            await this._deepFind(options, fn);
          }
          if (_.isArray(item[key])) {
            const options = {
              entry: item[key],
              key: opts.key,
              value: opts.value,
              matches: opts.matches,
            };
            await this._deepFind(options, fn);
          }
        }));
      }));
    },
    async _setAllProps(opts, collect, fn) {
      // loops an array of objects only
      await Promise.all(_.map(opts.entry, async (item) => {
        await Promise.all(_.map(item, async (value, key) => {
          if (key === opts.key) {
            // if we need to collect any items that were changed
            if (collect && collect.droplets.includes(item.type.split('.')[1])) {
              collect.list.push(item);
            }
            // run callbacks as needed
            if (fn) {
              await fn(item);
            }
          }
          // keep searching for props until the end
          // to find the keys in question
          if (_.isArray(item[key])) {
            const options = {
              entry: item[key],
              key: opts.key,
            };

            await this._setAllProps(options, collect, fn);
          }
        }));
      }));
    },
    _waitToFind(opts) {
      return new Promise((resolve) => {
        const item = this._goFind(opts);
        if (item.found) {
          resolve(item);
        } else {
          resolve(false);
        }
      });
    },
  },
};

export { searchParty }; // eslint-disable-line



// WEBPACK FOOTER //
// ./src/mixins/search-party.js